// ============================================================================
// FSA NOTE MODAL — SHARED CLASS LABEL EDITOR
// ============================================================================
// Inline form for adding a custom class, renaming a custom class, or
// renaming a template class (per-engagement label override). Extracted
// from MainNotePane so the multi-column class table (PPE, Intangibles,
// future Partnerships) can drive the SAME polished editor instead of
// falling back to window.prompt.
//
// Purely presentational — draft state lives in the parent (MainNotePane).
// The parent owns the workflow: pick mode + class → open editor → save
// commits via the parent's persistence callbacks → close.

import React from 'react';

export type ClassLabelEditorDraft =
  | { mode: 'add';             anchor: string;    name: string }
  | { mode: 'rename';          localId: string;   name: string }
  | { mode: 'rename-template'; classCode: string; name: string };

interface AnchorOption {
  /** Stable identifier — used as the `value` token under "AFTER:<id>". */
  classId: string;
  /** Display label for the option ("After: Land", "After: Plant & Machinery"). */
  className: string;
}

interface Props {
  draft: ClassLabelEditorDraft;
  /** Anchor options used when draft.mode = 'add' — ignored otherwise. */
  anchorClasses: AnchorOption[];
  onChange: (next: ClassLabelEditorDraft) => void;
  onConfirm: () => void;
  onCancel: () => void;
}

const ClassLabelEditor: React.FC<Props> = ({ draft, anchorClasses, onChange, onConfirm, onCancel }) => {
  const title =
    draft.mode === 'add'             ? 'Add Custom Class' :
    draft.mode === 'rename'          ? 'Rename Custom Class' :
                                       'Rename Template Class (this engagement only)';

  const hint =
    draft.mode === 'add'
      ? 'Adds an engagement-specific bucket at the same level as template classes. The auditor enters amounts manually.'
      : draft.mode === 'rename'
      ? 'Updates the printed label for this custom class.'
      : 'Saves a per-engagement label override. The template label stays canonical in the audit trail — only the modal display + this engagement\'s PDF show the new label.';

  const placeholder =
    draft.mode === 'add' ? 'e.g. "Foreign Currency Loans from Directors"' : '';

  const canSave = draft.name.trim().length > 0;

  return (
    <div style={customFormStyle}>
      <div style={customFormHeaderStyle}>
        {title}
        <span style={customFormHintStyle}>{hint}</span>
      </div>
      <div style={customFormRowStyle}>
        {draft.mode === 'add' && (
          <div style={customFormFieldStyle}>
            <label style={customFormLabelStyle}>Position</label>
            <select
              className="form-input"
              value={draft.anchor}
              onChange={e => onChange({ ...draft, anchor: e.target.value })}
              style={customFormInputStyle}
            >
              <option value="START">At start (before all classes)</option>
              {anchorClasses.map(s => (
                <option key={s.classId} value={`AFTER:${s.classId}`}>
                  After: {s.className}
                </option>
              ))}
              <option value="END">At end (after all classes)</option>
            </select>
          </div>
        )}
        <div style={{ ...customFormFieldStyle, flex: 2 }}>
          <label style={customFormLabelStyle}>Class name</label>
          <input
            type="text"
            className="form-input"
            value={draft.name}
            autoFocus
            placeholder={placeholder}
            onChange={e => onChange({ ...draft, name: e.target.value })}
            onKeyDown={e => {
              if (e.key === 'Enter' && canSave) {
                e.preventDefault();
                onConfirm();
              } else if (e.key === 'Escape') {
                e.preventDefault();
                onCancel();
              }
            }}
            style={customFormInputStyle}
          />
        </div>
        <div style={customFormActionsStyle}>
          <button type="button" className="btn btn-secondary btn-sm" onClick={onCancel}>
            Cancel
          </button>
          <button
            type="button"
            className="btn btn-primary btn-sm"
            onClick={onConfirm}
            disabled={!canSave}
          >
            Save
          </button>
        </div>
      </div>
    </div>
  );
};

// Styles — identical to MainNotePane's previous inline declarations so
// the editor is visually unchanged. Centralised here so the multi-column
// path doesn't need to re-declare them.
const customFormStyle: React.CSSProperties = {
  margin: '0 0 8px 0',
  padding: '10px 12px',
  background: 'var(--bg-secondary)',
  border: '1px solid var(--border-color)',
  borderLeft: '3px solid #2563eb',
  borderRadius: 6,
};
const customFormHeaderStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'baseline',
  flexWrap: 'wrap',
  gap: 8,
  marginBottom: 8,
  fontSize: '0.85rem',
  fontWeight: 600,
  color: 'var(--text-primary)',
};
const customFormHintStyle: React.CSSProperties = {
  fontSize: '0.72rem',
  fontWeight: 400,
  fontStyle: 'italic',
  color: 'var(--text-secondary)',
};
const customFormRowStyle: React.CSSProperties = {
  display: 'flex',
  alignItems: 'flex-end',
  gap: 8,
  flexWrap: 'wrap',
};
const customFormFieldStyle: React.CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  flex: 1,
  minWidth: 200,
};
const customFormLabelStyle: React.CSSProperties = {
  fontSize: '0.72rem',
  fontWeight: 600,
  color: 'var(--text-secondary)',
  marginBottom: 3,
  textTransform: 'uppercase',
  letterSpacing: 0.3,
};
const customFormInputStyle: React.CSSProperties = {
  fontSize: '0.85rem',
  padding: '6px 8px',
};
const customFormActionsStyle: React.CSSProperties = {
  display: 'flex',
  gap: 6,
  alignItems: 'flex-end',
};

export default ClassLabelEditor;
